import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis, Legend, PieChart, Pie, Cell } from "recharts";
import { usePortal } from "@/pages/admin/PortalLayout";

const COLORS = ["#8A1538", "#B8901F", "#0E8A73", "#2B569A", "#6B3884", "#C25A1C", "#455A64", "#C2185B"];

const Kpi = ({ label, value, testId, sub }) => (
  <div className="rounded-2xl border bg-card p-4 shadow-sm">
    <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
    <p className="mt-1 text-3xl font-extrabold text-primary" data-testid={testId}>{value}</p>
    {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
  </div>
);

export default function PortalDashboard() {
  const [d] = usePortal("/portal/dashboard");
  if (!d) return <div className="h-96 animate-pulse rounded-2xl bg-muted" />;
  const modes = Object.entries({ ...d.request_input_modes }).map(([name, value]) => ({ name: `requests: ${name}`, value }));
  const usage = Object.entries(d.usage_modes).map(([name, value]) => ({ name: name === "voice_query" ? "voice queries" : "typed searches", value }));
  return (
    <div className="space-y-6" data-testid="portal-dashboard">
      <div>
        <h1 className="text-2xl font-bold md:text-3xl">Analytics</h1>
        <p className="text-sm text-muted-foreground">Sandbox analytics across the service catalog and transaction DB.</p>
      </div>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi label="Services" value={d.services} sub={`${d.sectors} sectors`} testId="kpi-services" />
        <Kpi label="Requests" value={d.requests_total} sub={`${d.requests_completed} completed`} testId="kpi-requests" />
        <Kpi label="Completion rate" value={`${d.completion_rate}%`} testId="kpi-completion" />
        <Kpi label="Avg SLA" value={`${d.avg_sla_days}d`} sub={`avg fee ${d.avg_fee_qar} QAR`} testId="kpi-sla" />
        <Kpi label="Users" value={d.users} sub={`${d.groups} groups`} testId="kpi-users" />
        <Kpi label="Voice sessions" value={d.voice_sessions} testId="kpi-voice" />
        <Kpi label="Summaries played" value={d.summaries_played} testId="kpi-summaries" />
        <Kpi label="Languages used" value={d.languages.length} testId="kpi-languages" />
      </div>
      <div className="rounded-2xl border bg-card p-4 shadow-sm">
        <h2 className="mb-3 text-base font-bold md:text-lg">Requests by sector</h2>
        <div className="h-80" data-testid="chart-requests-by-sector">
          <ResponsiveContainer>
            <BarChart data={d.by_sector.map((s) => ({ name: s.sector_en.split(" ")[0], total: s.total, completed: s.completed }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} /><Tooltip /><Legend />
              <Bar dataKey="total" fill="#8A1538" radius={[6, 6, 0, 0]} /><Bar dataKey="completed" fill="#B8901F" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        {[["Voice vs typed", [...usage, ...modes]], ["Languages used", d.languages.map((l) => ({ name: l.lang, value: l.n }))]].map(([title, rows]) => (
          <div key={title} className="rounded-2xl border bg-card p-4 shadow-sm">
            <h2 className="mb-2 text-base font-bold md:text-lg">{title}</h2>
            {rows.length === 0 ? <p className="py-10 text-center text-sm text-muted-foreground">No events yet</p> : (
              <div className="h-64"><ResponsiveContainer><PieChart>
                <Pie data={rows} dataKey="value" nameKey="name" innerRadius={50} outerRadius={85} paddingAngle={2}>
                  {rows.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie><Tooltip /><Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart></ResponsiveContainer></div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
